// The Status page (GET /status): every data source's state, schedule and data date, grouped as the domain catalog
// (packages/domain/src/ingest/catalog.ts). Ids are label keys for the web; no internal source names or raw errors.
import { z } from 'zod';
import { IsoDate } from './accounts.ts';

export const SourceState = z.enum(['ok', 'waiting', 'stale', 'degraded', 'never', 'failed']);
export type SourceState = z.infer<typeof SourceState>;

export const StatusWarning = z.discriminatedUnion('code', [
  z.object({ code: z.enum(['never', 'failed', 'degraded', 'stale']) }),
  z.object({ code: z.literal('frozen'), since: IsoDate }),
  z.object({ code: z.literal('note'), key: z.string() }),
]);
export type StatusWarning = z.infer<typeof StatusWarning>;

const DatasetBase = z.object({
  id: z.string(),
  state: SourceState,
  frequency: z.enum(['daily', 'monthly', 'yearly']),
  frozenSince: IsoDate.nullable(),
  /** ISO timestamps (UTC); shown in São Paulo time. */
  lastCheckAt: z.string().nullable(),
  unchanged: z.boolean(),
  nextRunAt: z.string().nullable(),
  dataThrough: IsoDate.nullable(),
  dataPrecision: z.enum(['day', 'month', 'year']),
  warnings: z.array(StatusWarning),
});
export const DatasetStatus = DatasetBase.extend({ children: z.array(DatasetBase).optional() });
export type DatasetStatus = z.infer<typeof DatasetStatus>;

export const StatusResponse = z.object({
  checkedAt: z.string(),
  state: SourceState,
  /** Protocolo Digital outages under way now (ANM's own file). */
  outages: z.array(z.object({ startedAt: z.string(), resolvedAt: z.string().nullable() })),
  groups: z.array(z.object({ id: z.string(), module: z.enum(['ambiental', 'territorio']).optional(), state: SourceState, datasets: z.array(DatasetStatus) })),
});
export type StatusResponse = z.infer<typeof StatusResponse>;
